import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X as XIcon, Plus, Trash2, ChevronRight, ShoppingCart, Printer, Share2, Facebook, Twitter, Linkedin, Copy, Building2, HelpCircle, Users, Zap, Scale } from 'lucide-react';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { calculateSalePrice } from '../utils/calculateSalePrice';
import { getRegionPrice } from '../utils/getRegionPrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default competitor model data (anonymous) - can be overridden by admin
const defaultCompetitorModel = {
  id: 'competitor',
  name: 'Leading Competitor',
  image_url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80',
  price: 2995,
  specifications: {
    'Tray Space': '6 sq ft',
    'Capacity': '7-10 lbs fresh food',
    'Dimensions': '18.5"W x 21.5"D x 28.5"H',
    'Weight': '112 lbs',
    'Power': '110V / 15A',
    'Warranty': '3 Years Limited',
    'Pump Type': 'Oil-based',
    'Noise Level': '65 dB'
  },
  features: [
    'Stainless Steel Trays',
    'Touch Screen Controls',
    'Wi-Fi Connectivity'
  ],
  isCompetitor: true
};

// Wizard questions for guided selection
const wizardQuestions = [
  {
    id: 'experience',
    question: 'What is your freeze drying experience level?',
    options: [
      { value: 'beginner', label: 'Beginner', desc: 'New to freeze drying', icon: Users },
      { value: 'intermediate', label: 'Intermediate', desc: 'Some experience', icon: Zap },
      { value: 'advanced', label: 'Advanced', desc: 'Experienced user', icon: Scale }
    ]
  },
  {
    id: 'technical',
    question: 'How comfortable are you with technical equipment?',
    options: [
      { value: 'basic', label: 'Keep it Simple', desc: 'Prefer easy-to-use, set-it-and-forget-it operation', icon: Users },
      { value: 'moderate', label: 'Moderately Technical', desc: 'Comfortable with some adjustments and settings', icon: Zap },
      { value: 'advanced', label: 'Technically Savvy', desc: 'Enjoy fine-tuning and optimizing processes', icon: Scale }
    ]
  },
  {
    id: 'output',
    question: 'What are your output requirements?',
    options: [
      { value: 'personal', label: 'Personal/Family Use', desc: '10-20 lbs per batch', icon: Users },
      { value: 'small', label: 'Small Business', desc: '20-40 lbs per batch', icon: Zap },
      { value: 'commercial', label: 'Commercial Scale', desc: '40+ lbs per batch', icon: Scale }
    ]
  }
];

const CompareProducts = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeCompetitor, setIncludeCompetitor] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [competitors, setCompetitors] = useState([]);
  const [selectedCompetitor, setSelectedCompetitor] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState({});
  const [wizardReasoning, setWizardReasoning] = useState(null); // Store reasoning for recommendations
  const [wizardCompleted, setWizardCompleted] = useState(false); // Track if wizard was completed
  const { addItem } = useCart();
  const { formatPrice, formatMoney, currency } = useCurrency();
  const printRef = useRef(null);
  const shareRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/products`);
        setAllProducts(res.data.filter(p => p.category === 'freeze-dryer'));
        
        // Fetch competitors from backend
        try {
          const compRes = await axios.get(`${API_URL}/api/competitors`);
          if (compRes.data && compRes.data.length > 0) {
            setCompetitors(compRes.data);
          }
        } catch (e) {
          // Use default competitor if API not available
          setCompetitors([defaultCompetitorModel]);
        }
        
        // Check URL params for pre-selected products
        const ids = searchParams.get('ids');
        if (ids) {
          setSelectedIds(ids.split(', '));
        }
        // Wizard no longer auto-opens; users start it via the "Take Quiz" button.
      } catch (e) {
        console.error('Error fetching products:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [searchParams]);

  useEffect(() => {
    if (selectedIds.length >= 2 || (selectedIds.length >= 1 && includeCompetitor)) {
      fetchComparison();
    } else {
      setComparison(null);
    }
  }, [selectedIds, includeCompetitor]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchComparison = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/products/compare`, selectedIds);
      let products = res.data;
      
      // Add competitor if selected
      if (includeCompetitor) {
        const competitorToUse = selectedCompetitor || competitors[0] || defaultCompetitorModel;
        products = [...products, { ...competitorToUse, isCompetitor: true }];
      }
      
      // Build comparison object
      const allSpecs = new Set();
      const allFeatures = new Set();
      
      products.forEach(p => {
        if (p.specifications) {
          Object.keys(p.specifications).forEach(k => allSpecs.add(k));
        }
        if (p.features) {
          p.features.forEach(f => allFeatures.add(f));
        }
      });
      
      const featureMatrix = {};
      products.forEach(p => {
        featureMatrix[p.id] = p.features || [];
      });
      
      setComparison({
        products,
        spec_keys: Array.from(allSpecs),
        all_features: Array.from(allFeatures),
        feature_matrix: featureMatrix
      });
    } catch (e) {
      console.error('Error comparing products:', e);
    }
  };

  const addProduct = (productId) => {
    if (selectedIds.length >= 3) {
      toast.error('Maximum 3 products can be compared');
      return;
    }
    if (selectedIds.includes(productId)) {
      toast.error('Product already added');
      return;
    }
    setSelectedIds([...selectedIds, productId]);
  };

  const removeProduct = (productId) => {
    if (productId === 'competitor') {
      setIncludeCompetitor(false);
    } else {
      setSelectedIds(selectedIds.filter(id => id !== productId));
    }
  };

  const handleAddToCart = (product) => {
    if (product.isCompetitor) {
      toast.info('This is a competitor product for comparison only');
      return;
    }
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  const toggleCompetitor = () => {
    if (!includeCompetitor && selectedIds.length >= 3) {
      toast.error('Remove a product to add competitor comparison');
      return;
    }
    setIncludeCompetitor(!includeCompetitor);
  };

  // Print functionality
  const handlePrint = () => {
    window.print();
  };

  // Share functionality
  const shareUrl = window.location.href;
  const shareTitle = 'Compare D-Freeze Freeze Dryers';
  const shareText = 'Check out this freeze dryer comparison on D-Freeze!';

  const handleShare = (platform) => {
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        setShowShareMenu(false);
        return;
      default:
        return;
    }
    window.open(url, '_blank', 'width=600, height=400');
    setShowShareMenu(false);
  };

  // Wizard functions
  const handleWizardAnswer = (answer) => {
    const currentQuestion = wizardQuestions[wizardStep];
    setWizardAnswers({ ...wizardAnswers, [currentQuestion.id]: answer });
    
    if (wizardStep < wizardQuestions.length - 1) {
      setWizardStep(wizardStep + 1);
    } else {
      // Complete wizard and recommend products
      completeWizard({ ...wizardAnswers, [currentQuestion.id]: answer });
    }
  };

  const completeWizard = (answers) => {
    localStorage.setItem('compare_wizard_seen', 'true');
    setShowWizard(false);
    setWizardCompleted(true);
    
    // Build reasoning based on answers
    const reasoningParts = [];
    let recommendedIds = [];
    let competitorType = null;
    
    // Experience level reasoning
    if (answers.experience === 'beginner') {
      reasoningParts.push('As a beginner, we recommend user-friendly models with simpler controls');
    } else if (answers.experience === 'intermediate') {
      reasoningParts.push('With some experience, you can benefit from models with more customization options');
    } else if (answers.experience === 'advanced') {
      reasoningParts.push('As an advanced user, you\'ll appreciate models with full control and professional features');
    }
    
    // Technical comfort reasoning
    if (answers.technical === 'basic') {
      reasoningParts.push('We\'ve prioritized set-it-and-forget-it operation for ease of use');
    } else if (answers.technical === 'moderate') {
      reasoningParts.push('Selected models offer a good balance of control and simplicity');
    } else if (answers.technical === 'advanced') {
      reasoningParts.push('These models offer fine-grained control for optimization');
    }
    
    // Output requirements - main driver for product selection
    if (answers.output === 'personal') {
      reasoningParts.push('For personal/family use, smaller capacity models are most cost-effective');
      competitorType = 'home';
      // Recommend smaller/medium models for personal use
      const personalModels = allProducts.filter(p => 
        p.name.toLowerCase().includes('small') || 
        p.name.toLowerCase().includes('medium') ||
        (!p.name.toLowerCase().includes('commercial') && !p.name.toLowerCase().includes('xl'))
      );
      recommendedIds = personalModels.length > 0 
        ? personalModels.slice(0, 2).map(p => p.id)
        : allProducts.slice(0, 2).map(p => p.id);
    } else if (answers.output === 'commercial') {
      reasoningParts.push('For commercial scale, you need high-capacity industrial-grade equipment');
      competitorType = 'commercial';
      // Recommend larger/commercial models
      const commercialModels = allProducts.filter(p => 
        p.name.toLowerCase().includes('large') || 
        p.name.toLowerCase().includes('xl') ||
        p.name.toLowerCase().includes('commercial')
      );
      recommendedIds = commercialModels.length > 0
        ? commercialModels.slice(0, 2).map(p => p.id)
        : allProducts.slice(-2).map(p => p.id);
    } else {
      reasoningParts.push('For small business use, mid-range models offer the best value and throughput');
      competitorType = 'small-business';
      // Medium output - show mid-range (pick from middle of array if possible)
      const midIndex = Math.floor(allProducts.length / 2);
      recommendedIds = allProducts.length >= 2 
        ? [allProducts[Math.max(0, midIndex - 1)].id, allProducts[Math.min(allProducts.length - 1, midIndex)].id]
        : allProducts.slice(0, 2).map(p => p.id);
    }
    
    // Set the reasoning
    setWizardReasoning({
      summary: reasoningParts.join('. ') + '.',
      answers: {
        experience: answers.experience,
        technical: answers.technical,
        output: answers.output
      },
      showBusinessLink: answers.output === 'commercial' || answers.output === 'small'
    });
    
    // Find and auto-include a matching competitor
    const availableCompetitors = competitors.length > 0 ? competitors : [defaultCompetitorModel];
    
    if (competitorType) {
      // Try to find a competitor matching the type
      const matchingCompetitor = availableCompetitors.find(c => 
        c.competitor_type?.toLowerCase() === competitorType.toLowerCase() ||
        c.description?.toLowerCase().includes(competitorType.toLowerCase())
      );
      
      if (matchingCompetitor) {
        setSelectedCompetitor(matchingCompetitor);
        setIncludeCompetitor(true);
      } else {
        // Fall back to first available competitor
        setSelectedCompetitor(availableCompetitors[0]);
        setIncludeCompetitor(true);
      }
    }
    
    if (recommendedIds.length > 0) {
      setSelectedIds(recommendedIds);
      toast.success('We\'ve selected products and a competitor based on your needs!');
    }
  };

  // Function to retake the wizard
  const retakeWizard = () => {
    setWizardStep(0);
    setWizardAnswers({});
    setWizardReasoning(null);
    setWizardCompleted(false);
    setShowWizard(true);
    // Clear current selections when retaking
    setSelectedIds([]);
    setIncludeCompetitor(false);
    setSelectedCompetitor(null);
  };

  const closeWizard = () => {
    localStorage.setItem('compare_wizard_seen', 'true');
    setShowWizard(false);
  };

  // Close the wizard with the Escape key
  useEffect(() => {
    if (!showWizard) return;
    const onKey = (e) => { if (e.key === 'Escape') closeWizard(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showWizard]);

  // Helper function to get sale price info for a product
  const getProductPriceInfo = (product) => {
    if (product.isCompetitor) {
      return { price: product.price, isOnSale: false };
    }
    const saleInfo = calculateSalePrice(product, currency);
    const regionMsrp = getRegionPrice(product, currency);
    return {
      price: saleInfo.isOnSale ? saleInfo.effectivePrice : regionMsrp,
      originalPrice: regionMsrp,
      isOnSale: saleInfo.isOnSale,
      discountPercent: saleInfo.savingsPercent
    };
  };

  const availableProducts = allProducts.filter(p => !selectedIds.includes(p.id));
  const totalCompareSlots = includeCompetitor ? selectedIds.length + 1 : selectedIds.length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Print styles */}
      <style>{`
        @media print {
          nav, footer, .no-print { display: none !important; }
          .print-section { padding: 0 !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="pt-44 pb-16 section-padding print-section" ref={printRef}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 animate-fade-in no-print">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0a0203] mb-4">
                Compare Freeze Dryers
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                Select up to 3 freeze dryers to compare specifications, features, and pricing side by side.
              </p>
            </div>
            
            {/* Print & Share Buttons */}
            {comparison && (
              <div className="flex items-center gap-3 mt-4 md:mt-0">
                {/* Retake Quiz button when wizard was NOT completed */}
                {!wizardCompleted && (
                  <Button
                    onClick={retakeWizard}
                    variant="outline"
                    className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white"
                    data-testid="take-quiz-btn"
                  >
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Take Quiz
                  </Button>
                )}
                
                <Button
                  onClick={handlePrint}
                  variant="outline"
                  className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white"
                  data-testid="print-comparison-btn"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                
                <div className="relative" ref={shareRef}>
                  <Button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    variant="outline"
                    className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white"
                    data-testid="share-comparison-btn"
                  >
                    <Share2 className="h-4 w-4 mr-2" />
                    Share
                  </Button>
                  
                  {showShareMenu && (
                    <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px] z-50">
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#F8FAFC] flex items-center gap-3"
                      >
                        <Facebook className="h-4 w-4 text-blue-600" />
                        Facebook
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#F8FAFC] flex items-center gap-3"
                      >
                        <Twitter className="h-4 w-4 text-sky-500" />
                        Twitter
                      </button>
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#F8FAFC] flex items-center gap-3"
                      >
                        <Linkedin className="h-4 w-4 text-blue-700" />
                        LinkedIn
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                      <button
                        onClick={() => handleShare('copy')}
                        className="w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#F8FAFC] flex items-center gap-3"
                      >
                        <Copy className="h-4 w-4" />
                        Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Wizard Reasoning Panel - shows after wizard completion */}
          {wizardReasoning && wizardCompleted && (
            <div className="bg-gradient-to-r from-[#dbeef5] to-white rounded-2xl p-6 mb-8 shadow-sm animate-fade-in no-print border border-[#003456]/20">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#003456] rounded-full flex items-center justify-center flex-shrink-0">
                    <HelpCircle className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0a0203] mb-2">
                      Why We Recommend These
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {wizardReasoning.summary}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-[#003456]/10 text-[#003456]">
                        Experience: {wizardReasoning.answers.experience}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-[#003456]/10 text-[#003456]">
                        Technical: {wizardReasoning.answers.technical}
                      </span>
                      <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-[#003456]/10 text-[#003456]">
                        Output: {wizardReasoning.answers.output}
                      </span>
                    </div>
                    
                    {/* Commercial/Business Link */}
                    {wizardReasoning.showBusinessLink && (
                      <div className="mt-4 pt-4 border-t border-[#003456]/10">
                        <Link 
                          to="/business-solutions" 
                          className="inline-flex items-center gap-2 text-[#003456] hover:text-[#002B47] font-medium transition-colors"
                          data-testid="business-solutions-link"
                        >
                          <Building2 className="h-5 w-5" />
                          <span>Explore Commercial & Industrial Solutions</span>
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                        <p className="text-sm text-gray-600 mt-1">
                          Looking for larger volumes or business features? Check out our dedicated commercial options.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  onClick={retakeWizard}
                  variant="outline"
                  size="sm"
                  className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white flex-shrink-0"
                  data-testid="retake-wizard-btn"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Retake Quiz
                </Button>
              </div>
            </div>
          )}

          {/* Product Selector */}
          {totalCompareSlots < 3 && (
            <div className="bg-white rounded-2xl p-6 mb-8 shadow-sm animate-fade-in no-print">
              <h3 className="text-lg font-semibold text-[#0a0203] mb-4">
                Add Products to Compare ({totalCompareSlots}/3)
              </h3>
              <div className="flex flex-wrap gap-3">
                {availableProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product.id)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#F8FAFC] rounded-full hover:bg-[#dbeef5] transition-colors"
                    data-testid={`add-compare-${product.id}`}
                  >
                    <Plus className="h-4 w-4 text-[#003456]" />
                    <span className="text-sm font-medium">{product.name}</span>
                  </button>
                ))}
                
                {/* Add Competitor Option */}
                {!includeCompetitor && (
                  <button
                    onClick={toggleCompetitor}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors border-2 border-dashed border-gray-300"
                    data-testid="add-competitor-btn"
                  >
                    <Building2 className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-600">Add Competitor Model</span>
                  </button>
                )}
              </div>
              
              {includeCompetitor && (
                <p className="mt-3 text-sm text-gray-500">
                  * Competitor data is representative of leading industry alternatives
                </p>
              )}
            </div>
          )}

          {/* Comparison Table */}
          {comparison && comparison.products && comparison.products.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden animate-fade-in">
              {/* Product Headers */}
              <div className="grid" style={{ gridTemplateColumns: `200px repeat(${comparison.products.length}, 1fr)` }}>
                <div className="p-6 bg-gray-50 border-b border-r border-gray-200"></div>
                {comparison.products.map(product => {
                  const priceInfo = getProductPriceInfo(product);
                  return (
                  <div key={product.id} className={`p-6 border-b border-r border-gray-200 last:border-r-0 relative ${product.isCompetitor ? 'bg-gray-50' : ''}`}>
                    <button
                      onClick={() => removeProduct(product.id)}
                      className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 no-print"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    
                    {product.isCompetitor && (
                      <div className="absolute top-2 left-2 bg-gray-200 text-gray-600 text-xs px-2 py-1 rounded">
                        Competitor
                      </div>
                    )}
                    
                    {priceInfo.isOnSale && !product.isCompetitor && (
                      <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded font-bold">
                        SALE
                      </div>
                    )}
                    
                    <div className="w-full h-48 mb-4 flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className={`max-w-full max-h-full object-contain ${product.isCompetitor ? 'grayscale opacity-70' : ''}`}
                      />
                    </div>
                    <h3 className="font-semibold text-[#0a0203] mb-3 text-center">
                      {product.name}
                    </h3>
                    
                    {/* Price with Sale Support */}
                    <div className="text-center mb-4">
                      {priceInfo.isOnSale ? (
                        <>
                          <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatMoney(priceInfo.price)}
                          </p>
                          <p className="text-sm text-gray-400 line-through" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatMoney(priceInfo.originalPrice)}
                          </p>
                          <p className="text-xs text-emerald-600 font-semibold">
                            Save {priceInfo.discountPercent}%
                          </p>
                        </>
                      ) : (
                        <p className="text-2xl font-bold text-[#003456]" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatMoney(priceInfo.price)}
                        </p>
                      )}
                    </div>
                    
                    {!product.isCompetitor ? (
                      <Button
                        onClick={() => handleAddToCart(product)}
                        className="w-full bg-[#003456] hover:bg-[#002B47] text-white no-print"
                        data-testid={`add-cart-${product.id}`}
                      >
                        Add to Cart
                      </Button>
                    ) : (
                      <p className="text-center text-sm text-gray-500 italic">
                        For comparison only
                      </p>
                    )}
                  </div>
                  );
                })}
              </div>

              {/* Specifications */}
              <div className="grid" style={{ gridTemplateColumns: `200px repeat(${comparison.products.length}, 1fr)` }}>
                <div className="p-4 bg-[#003456] text-white font-semibold border-r border-gray-200" style={{ gridColumn: '1 / -1' }}>
                  Specifications
                </div>
              </div>
              
              {comparison.spec_keys.map((specKey, idx) => (
                <div 
                  key={specKey} 
                  className="grid" 
                  style={{ gridTemplateColumns: `200px repeat(${comparison.products.length}, 1fr)` }}
                >
                  <div className={`p-4 font-medium text-gray-700 border-r border-b border-gray-200 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    {specKey}
                  </div>
                  {comparison.products.map(product => (
                    <div 
                      key={product.id} 
                      className={`p-4 border-r border-b border-gray-200 last:border-r-0 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'} ${product.isCompetitor ? 'text-gray-500' : ''}`}
                      style={{ fontFamily: 'JetBrains Mono' }}
                    >
                      {product.specifications?.[specKey] || '-'}
                    </div>
                  ))}
                </div>
              ))}

              {/* Features */}
              <div className="grid" style={{ gridTemplateColumns: `200px repeat(${comparison.products.length}, 1fr)` }}>
                <div className="p-4 bg-[#003456] text-white font-semibold border-r border-gray-200" style={{ gridColumn: '1 / -1' }}>
                  Features
                </div>
              </div>
              
              {comparison.all_features.map((feature, idx) => (
                <div 
                  key={feature} 
                  className="grid" 
                  style={{ gridTemplateColumns: `200px repeat(${comparison.products.length}, 1fr)` }}
                >
                  <div className={`p-4 font-medium text-gray-700 border-r border-b border-gray-200 ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    {feature}
                  </div>
                  {comparison.products.map(product => (
                    <div 
                      key={product.id} 
                      className={`p-4 border-r border-b border-gray-200 last:border-r-0 flex items-center justify-center ${idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      {comparison.feature_matrix[product.id]?.includes(feature) ? (
                        <Check className={`h-6 w-6 ${product.isCompetitor ? 'text-gray-400' : 'text-[#003456]'}`} />
                      ) : (
                        <XIcon className="h-6 w-6 text-gray-300" />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-16 text-center shadow-sm">
              <div className="w-20 h-20 mx-auto mb-6 bg-[#dbeef5] rounded-full flex items-center justify-center">
                <ShoppingCart className="h-10 w-10 text-[#003456]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0a0203] mb-2">
                Select Products to Compare
              </h3>
              <p className="text-gray-500 mb-6">
                Choose at least 2 freeze dryers from above to see a detailed comparison, or let our wizard help you find the right match.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={retakeWizard}
                  className="bg-[#003456] hover:bg-[#002B47] text-white"
                  data-testid="start-wizard-btn"
                >
                  <HelpCircle className="h-4 w-4 mr-2" />
                  Help Me Choose
                </Button>
                <Link to="/products">
                  <Button variant="outline" className="border-[#003456] text-[#003456]">
                    Browse Products
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 bg-[#003456] rounded-2xl p-8 md:p-12 text-center text-white no-print">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Need Help Choosing?
            </h2>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Our freeze drying experts can help you find the perfect model for your needs.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#003456] hover:bg-gray-100">
                Talk to an Expert
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Wizard Popup */}
      {showWizard && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeWizard}
          data-testid="compare-wizard-backdrop"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full p-8 animate-fade-in shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Find your perfect match"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#003456] rounded-full flex items-center justify-center">
                  <HelpCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0a0203]">Find Your Perfect Match</h2>
                  <p className="text-sm text-gray-500">Step {wizardStep + 1} of {wizardQuestions.length}</p>
                </div>
              </div>
              <button
                onClick={closeWizard}
                className="text-gray-400 hover:text-gray-600"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
              <div 
                className="bg-[#003456] h-2 rounded-full transition-all"
                style={{ width: `${((wizardStep + 1) / wizardQuestions.length) * 100}%` }}
              />
            </div>
            
            <h3 className="text-lg font-semibold text-[#0a0203] mb-4">
              {wizardQuestions[wizardStep].question}
            </h3>
            
            <div className="space-y-3">
              {wizardQuestions[wizardStep].options.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleWizardAnswer(option.value)}
                    className="w-full flex items-center gap-4 p-4 border-2 border-gray-200 rounded-xl hover:border-[#003456] hover:bg-[#F8FAFC] transition-all text-left"
                    data-testid={`wizard-option-${option.value}`}
                  >
                    <div className="w-12 h-12 bg-[#dbeef5] rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="h-6 w-6 text-[#003456]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0a0203]">{option.label}</p>
                      <p className="text-sm text-gray-500">{option.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            
            <div className="mt-6 flex justify-between items-center">
              {wizardStep > 0 ? (
                <button
                  onClick={() => setWizardStep(wizardStep - 1)}
                  className="text-[#003456] hover:underline text-sm"
                >
                  ← Back
                </button>
              ) : (
                <div />
              )}
              <button
                onClick={closeWizard}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CompareProducts;
